import {
  Alignment,
  EventCallback,
  EventType,
  Fit,
  Layout,
  Event as RiveEvent,
  StateMachineInput,
  StateMachineInputType,
  useRive,
} from '@rive-app/react-canvas';
import { captureException } from '@sentry/react';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { ConditionalWrap } from '@frontend-libs/atomic';

import { RiveAnimationTheme } from './rive-animation-theme.component';

const applyRiveValue = (
  input: StateMachineInput,
  value: number | boolean | undefined,
) => {
  switch (input.type) {
    case StateMachineInputType.Trigger:
      input.fire();
      break;
    case StateMachineInputType.Number:
      input.value = typeof value === 'number' ? value : 0;
      break;
    case StateMachineInputType.Boolean:
      input.value = typeof value === 'boolean' ? value : !input.value;
      break;
  }
};

/**
 * To trigger an animation event, use the `trigger` method.
 * const riveRef = useRef<RiveAnimationRef>(null);
 * const handleTrigger = (inputName: string) => riveRef.current?.trigger(inputName)
 */
export interface RiveAnimationRef {
  trigger: (name: string, value?: number | boolean) => void;
}

export interface RiveAnimationProps {
  src: string;
  height: string | number;
  width?: string | number;
  stateMachine?: string;
  initialInputValues?: Record<string, number | boolean>;
  onEvent?: (event: RiveEvent) => void;
  color?: string;
  /**
   * Stops rendering once nothing has been triggered for this many ms. The web runtime otherwise redraws
   * every frame while on screen, even a still image. Only for files that settle (not spinners).
   */
  freezeAfterMs?: number;
}

export const RiveAnimation = forwardRef<RiveAnimationRef, RiveAnimationProps>(
  (props, ref) => {
    const riveInputs = useRef<StateMachineInput[] | undefined>(undefined);
    const activeUntil = useRef(0);
    const [queuedTriggers, setQueuedTriggers] = useState<
      { name: string; value: number | boolean | undefined }[]
    >([]);

    const { RiveComponent, rive } = useRive({
      src: props.src,
      autoplay: props.initialInputValues === undefined, // rive animation cannot be playing when setting initial input values
      stateMachines: props.stateMachine ?? 'State Machine 1',
      layout: new Layout({
        alignment: Alignment.Center,
        fit: Fit.FitHeight,
      }),
      autoBind: !!props.color,
      onLoadError: (e) => {
        captureException(e, {
          level: 'warning',
          extra: {
            message: `Failed to load rive animation from source: ${props.src}`,
          },
        });
      },
    });

    useEffect(() => {
      if (!rive) {
        return;
      }

      activeUntil.current = performance.now() + (props.freezeAfterMs ?? 0);

      const inputs = rive?.stateMachineInputs(
        props.stateMachine ?? 'State Machine 1',
      );

      if (inputs) {
        if (props.initialInputValues !== undefined) {
          setInitialInputValues(inputs);
        }

        riveInputs.current = inputs;

        if (queuedTriggers.length > 0) {
          for (const queuedTrigger of queuedTriggers) {
            const input = inputs.find(
              (input) =>
                input.name.toLowerCase() === queuedTrigger.name.toLowerCase(),
            );

            input && applyRiveValue(input, queuedTrigger.value);
          }

          setQueuedTriggers([]);
        }
      }

      const handleRiveEvent: EventCallback = (event) => {
        props.onEvent?.(event);
      };

      // Checked every frame: useRive restarts rendering on resize and when scrolled back into view.
      const handleAdvance: EventCallback = () => {
        if (
          props.freezeAfterMs !== undefined &&
          performance.now() > activeUntil.current
        ) {
          // Deferred: the runtime requests its next frame after this callback returns.
          queueMicrotask(() => rive.stopRendering());
        }
      };

      rive.on(EventType.RiveEvent, handleRiveEvent);
      rive.on(EventType.Advance, handleAdvance);

      return () => {
        rive.off(EventType.RiveEvent, handleRiveEvent);
        rive.off(EventType.Advance, handleAdvance);
      };
    }, [rive, queuedTriggers]);

    const setInitialInputValues = (inputs: StateMachineInput[]) => {
      inputs.forEach((input) => {
        const initialValue = props.initialInputValues![input.name];

        if (initialValue !== undefined) {
          switch (input.type) {
            case StateMachineInputType.Number:
              input.value = Number(initialValue);
              break;
            case StateMachineInputType.Boolean:
              input.value = Boolean(initialValue);
              break;
            default:
              break;
          }
        }
      });

      // rive animation cannot be playing when setting initial input values
      if (rive && !rive.isPlaying) {
        rive.play();
      }
    };

    useImperativeHandle(ref, () => {
      return {
        trigger: (name: string, value?: number | boolean) => {
          const input = riveInputs?.current?.find(
            (input) => input.name.toLowerCase() === name.toLowerCase(),
          );

          if (input === undefined) {
            setQueuedTriggers((prev) => {
              if (prev.find((t) => t.name === name && t.value === value)) {
                return prev;
              }

              return [
                ...prev,
                {
                  name,
                  value,
                },
              ];
            });
            return false;
          }

          applyRiveValue(input, value);
          activeUntil.current = performance.now() + (props.freezeAfterMs ?? 0);
          rive?.startRendering();

          return true;
        },
      };
    });

    return (
      <ConditionalWrap
        condition={!!props?.color}
        wrap={(children) => (
          <RiveAnimationTheme rive={rive} color={props.color!}>
            {children}
          </RiveAnimationTheme>
        )}
      >
        <RiveComponent
          style={{
            width: props.width ?? '100%',
            height: props.height,
          }}
        />
      </ConditionalWrap>
    );
  },
);
